import { useNavigate, useSearch } from '@tanstack/react-router';
import * as React from 'react';
import {
  AnalyticsTab,
  MattersTab,
  PromptsTab,
  SyncsTab,
  TemplatesTab,
} from '@/components/admin/firm';
import Loading from '@/components/shared/Loading';
import { HeaderTabs } from '@/components/ui/HeaderTabs';
import { useShellHeader, useShellHeaderBelow } from '@/components/contexts/ShellHeaderContext';
import { useAdminCheck } from '@/hooks/useAdminCheck';
import { FEATURE_FLAGS, useFeatures } from '@/hooks/useFeatures';
import type { AdminTab } from '@/src/router/search-schemas';

const DEFAULT_TAB: AdminTab = 'analytics';

const AdminPage = () => {
  const navigate = useNavigate();
  const search = useSearch({ from: '/admin' });
  const { isAdmin, isLoading: isCheckingAdmin } = useAdminCheck();
  const { isEnabled: draftingEnabled, isValidating: isFeaturesValidating } = useFeatures(FEATURE_FLAGS.DRAFTING);
  const canViewTemplates = draftingEnabled;
  const [analyticsHeaderActions, setAnalyticsHeaderActions] = React.useState<React.ReactNode | null>(null);

  const activeTab: AdminTab = search.tab ?? DEFAULT_TAB;

  useShellHeader({
    header: 'Admin',
    headerActions: activeTab === 'analytics' ? analyticsHeaderActions : null,
  });

  const adminHeaderItems = React.useMemo(() => [
    { value: 'analytics', label: 'Analytics' },
    { value: 'matters', label: 'Matters' },
    ...(canViewTemplates ? [{ value: 'templates', label: 'Templates' }] : []),
    { value: 'prompts', label: 'Prompts' },
    { value: 'syncs', label: 'Syncs' },
  ], [canViewTemplates]);

  const setTab = React.useCallback((tab: AdminTab) => {
    void navigate({
      to: '/admin',
      search: { tab },
    });
  }, [navigate]);

  const adminHeaderBelow = React.useMemo(() => (
    <HeaderTabs
      items={adminHeaderItems}
      value={activeTab}
      onChange={(value) => setTab(value as AdminTab)}
      ariaLabel="Admin sections"
      idBase="firm-admin"
    />
  ), [activeTab, adminHeaderItems, setTab]);

  useShellHeaderBelow(adminHeaderBelow);

  React.useEffect(() => {
    if (isCheckingAdmin) return;
    if (!isAdmin) {
      void navigate({ to: '/home' });
      return;
    }
    // Templates tab only when drafting is enabled — wait for /features so a
    // cold cache does not bounce ?tab=templates to analytics prematurely.
    if (activeTab === 'templates' && !canViewTemplates) {
      if (isFeaturesValidating) return;
      void navigate({ to: '/admin', search: { tab: DEFAULT_TAB }, replace: true });
    }
  }, [activeTab, canViewTemplates, isAdmin, isCheckingAdmin, isFeaturesValidating, navigate]);

  const handleAnalyticsActions = React.useCallback((actions: React.ReactNode | null) => {
    setAnalyticsHeaderActions(actions);
  }, []);

  if (isCheckingAdmin) {
    return (
      <div className="flex h-56 items-center justify-center">
        <Loading />
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <div role="tabpanel" id={`firm-admin-panel-${activeTab}`} aria-labelledby={`firm-admin-tab-${activeTab}`}>
      {activeTab === 'analytics' && (
        <AnalyticsTab onHeaderActionsChange={handleAnalyticsActions} />
      )}
      {activeTab === 'matters' && <MattersTab />}
      {activeTab === 'templates' && canViewTemplates && <TemplatesTab />}
      {activeTab === 'prompts' && <PromptsTab />}
      {activeTab === 'syncs' && <SyncsTab />}
    </div>
  );
};

export default AdminPage;
